import type { ExternalDestination } from '../../shared/modules/external-sharing/models/external-sharing.models';
import type { TransKey } from '../services/translate/translate.model';
import type {
  Attachment,
  BriefProject,
  BriefWithCountsProject,
  DateFormatEnum,
  FilteredTableId,
  InternalLariItemDetail,
  InternalLariItemId,
  LariItemComment,
  LariItemReference,
  NestedLariItemTimeLineEvent,
  ProjectForLariSearch,
  SlimFilteredTable,
  SlimTable,
  TableId,
} from '../api/schema';
import type { Mutable, Override, ReadModel } from '../api/adapters';
import { DocumentTypeEnum } from '../api/schema.enums';
import type { SearchItemAttributes } from '../api/schema.enums';

// Brand ids sourced from the backend spec (stamped via `x-brand`); re-exported so existing
// consumers importing them from here are unchanged.
export type { FilteredTableId, InternalLariItemId, TableId };

export type SerializableType = string | number | boolean;
/** A query-string bag: what `convertObjToQueryString` accepts. */
export type GenericObj = Record<string, SerializableType | SerializableType[] | Set<SerializableType> | undefined>;

// A merge patch. `Partial<T>` can't express one under `exactOptionalPropertyTypes`: it drops
// `undefined` from keys that are required on `T`, which a patch must be able to send.
export type Patch<T> = { [K in keyof T]?: T[K] | undefined };

export type IAttachment = Mutable<Attachment>;

export type ProjectId = number & { __projectIdBrand: never };

export type IssueId = number & { __issueIdBrand: never };

/**
 * Minimal project reference emitted by the backend `ProjectForLariSearchSerializer`
 * (`url`/`id`/`name` only) — used for search embeds and project-tag `project`
 * fields. Nothing else is serialized in those contexts.
 */
export type IProjectRef = ReadModel<ProjectForLariSearch, ProjectId>;

type IProjectCounts = Partial<Omit<BriefWithCountsProject, keyof BriefProject>>;

export type IProjectBase = ReadModel<BriefProject, ProjectId> & IProjectCounts;

export type IAttachedTable = ReadModel<SlimTable, TableId>;

export type MonitoringTopicId = number & { __monitoringTopicIdBrand: never };

// Backend document-type enum, emitted uniformly by the spec (every `document_type` field
// `$ref`s the shared `DocumentTypeEnum`).
export { DocumentTypeEnum };

// Backend `LariItemReference` — the reference embed. `Mutable` for the edit form (which rebuilds
// these); the wire has no `readonly` fields, so it's a structural no-op today. Every reader touches
// only `title`/`url`.
export type ISearchReference = Mutable<LariItemReference>;

// Edit-form shape for one schedule row — deliberately NOT the wire
// `NestedInternalLariItemSchedule` that `IInternalItem.schedule` carries. The
// mat-datepicker binds `Date` objects into `dates`/`maybe_dates`: a loaded item starts holding
// the wire `DateString`s, a freshly-picked cell becomes a `Date`, a cleared cell `null`. Values
// are serialised back to `DateString` only on save (`getSchedule`).
export interface InternalItemSchedule {
  url: string | null;
  title: string;
  dates: (Date | string | null)[];
  maybe_dates: (Date | string | null)[];
}

// Minimal user reference used by types defined here that cannot depend on user/models/user (cycle prevention)
export interface IUserRef {
  id: number;
  display_name?: string;
  email: string;
  avatar: string;
  first_name: string;
  last_name: string;
}

// Backend `NestedLariItemTimeLineEvent` (the nested embed carried by items/search hits).
// `Mutable` drops the wire's `readonly` (consumers rebuild these into forms/diffs); the
// branded `id`, `SlimLariUser` refs, and branded date strings all come through for free.
export type ITimelineEvent = Mutable<NestedLariItemTimeLineEvent>;

// An event being edited: a brand-new one has no id/url yet, and both edited fields carry raw
// form values (so `date` has lost the wire's `DateString` brand).
export type ITimelineEventDraft = Omit<Partial<ITimelineEvent>, 'date' | 'text'> & {
  date: string | null;
  text: string | null;
};

interface IMainLariItem {
  larisearch_id: string;
  // Wire `SlimLariItem.comment` is a `LariItemComment` object (or null), NOT a string:
  // every consumer already reads `.description` / tests truthiness (never uses it as a
  // string), and `search.ts` types the same field as `ILariItemComment`.
  comment: Mutable<LariItemComment> | null;
  timeline_events: ITimelineEvent[];
  external_destinations: ExternalDestination[];
}

// The internal-item read model, anchored to the wire `InternalLariItemDetail` (what `getItem`
// returns). `ReadModel` brands the id + drops `readonly`; every scalar/date/array field comes
// straight from the wire: `description`/`body` are `string | null`, `publication_date` is non-null,
// and `chronology` is read by the edit form via `INTERNAL_FIELDS_OPTIONS`. The only FE divergence
// is `main_lari_item` — the FE view (`IMainLariItem`) with comment/timeline as FE view types.
export type IInternalItem = Override<ReadModel<InternalLariItemDetail, InternalLariItemId>, { main_lari_item: IMainLariItem }>;

// Backend `SlimFilteredTable` — the exact slim embed the issue payload carries in
// `filtered_tables` (see `issue-back-to-front-convert.util`). `Mutable` drops `readonly`;
// `id` is already the branded `FilteredTableId`.
export type IFilteredTableInIssue = Mutable<SlimFilteredTable>;

export const EMPTY_VALUE = 'EMPTY_VALUE';

export enum TableFilterModelTypeKeys {
  Equals = 'equals',
  NotEqual = 'notEqual',
  NoStatus = 'noStatus',
  NoTag = 'noTag',
  NoUser = 'noUser',
  NoAuthor = 'noAuthor',
  NoLabel = 'noLabel',
  NoIssue = 'noIssue',
  NoTable = 'noTable',
}

export type TableFilterComparisonKey = TableFilterModelTypeKeys.Equals | TableFilterModelTypeKeys.NotEqual;
export type TableFilterNoValueKey = Exclude<TableFilterModelTypeKeys, TableFilterComparisonKey>;

export type SearchItemMultiStringAttribute = SearchItemAttributes.tags | SearchItemAttributes.authors | SearchItemAttributes.status;

export enum TableFilterCSSClasses {
  Schedule = 'table-schedule-filter',
  Chronology = 'table-chronology-filter',
  SearchItemsStrings = 'table-strings-filter',
  Entity = 'table-entity-filter',
  Date = 'table-date-filter',
}

export { UserTypeEnum } from '../api/schema.enums';

export interface IInternalTemplateOption {
  display: string;
  value: string;
}

export enum ExportLayout {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

// Kept a named enum (readable members + the runtime-replaced `Local` sentinel) rather than
// aliasing the wire type: `'L'` is a FE-only marker (`date-time.service` swaps it for the
// user's locale format) and isn't a backend value. The guard below reuses the backend
// `DateFormatEnum` as source of truth, so any drift from it fails the build.
export enum DateFormats {
  A = 'yyyy-MM-dd',
  B = 'd M yy',
  C = 'd.M.yy',
  D = 'd/M/yy',
  E = 'dd MM yyyy',
  F = 'dd.MM.yyyy',
  G = 'dd/MM/yyyy',
  H = 'MM/dd/yyyy',
  Local = 'L',
}

// Compile-time guard: `DateFormats` must be exactly the backend `DateFormatEnum` plus the
// FE-only `'L'`. `AssertExtends<T, U>` errors if `T` isn't assignable to `U`, so the build
// breaks if the FE enum drifts from the backend (a stray FE format, or a new backend one
// left uncovered). Exported only so it counts as used; it has no runtime footprint.
type AssertExtends<T extends U, U> = T;
/** @knipignore */
export type DateFormatsMatchBackend = [
  AssertExtends<`${DateFormats}`, DateFormatEnum | 'L'>,
  AssertExtends<DateFormatEnum, `${DateFormats}`>,
];

export enum TimeFormats {
  A = 'HH:mm:ss',
  B = 'H:m:s',
  C = 'hh:mm:ss a',
  Local = 'LTS',
}

export interface IDateVariation {
  date_format: DateFormats | '';
}

interface ITimeVariation {
  time_format: TimeFormats | '';
}

export interface IDateTimeVariation extends IDateVariation, ITimeVariation {
  datetime_format: `${DateFormats} ${TimeFormats}` | '';
  prefix?: TransKey | undefined;
}

export interface IDateTimeConfig {
  date: IDateVariation['date_format'];
  time: IDateTimeVariation['time_format'];
  dateTime: IDateTimeVariation['datetime_format'];
  prefix?: TransKey | undefined;
}

export class DateTimeVariation implements IDateTimeVariation {
  date_format: IDateVariation['date_format'];
  time_format: IDateTimeVariation['time_format'];
  datetime_format: IDateTimeVariation['datetime_format'];
  prefix: TransKey | undefined;
  constructor(config: IDateTimeConfig) {
    this.date_format = config.date;
    this.time_format = config.time;
    this.datetime_format = config.dateTime;
    this.prefix = config.prefix;
  }
}

export interface DateTimeOption {
  value: IDateTimeVariation;
  display: string;
  format: keyof IDateTimeVariation;
}
